import React, { createContext, useContext, useState, useEffect, useMemo } from "react"
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles"
import { erpMinimalTheme, erpColorTheme, erpEnterpriseTheme, erpElitonTheme } from "./flexflowTheme"

// Theme options
export const THEME_OPTIONS = {
  ERP_MINIMAL: "erp-minimal",
  ERP_COLOR: "erp-color",
  ERP_ENTERPRISE: "erp-enterprise",
  ERP_ELITON: "erp-eliton",
}

// Theme labels for display
export const THEME_LABELS = {
  [THEME_OPTIONS.ERP_MINIMAL]: "ERP - Minimal",
  [THEME_OPTIONS.ERP_COLOR]: "ERP - Color",
  [THEME_OPTIONS.ERP_ENTERPRISE]: "ERP - Enterprise",
  [THEME_OPTIONS.ERP_ELITON]: "Eliton",
}

// Theme context
const ThemeContext = createContext({
  currentTheme: THEME_OPTIONS.ERP_MINIMAL,
  setTheme: () => {},
})

// Hook to use theme context
export const useThemeContext = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useThemeContext must be used within ThemeContextProvider")
  }
  return context
}

// Theme context provider
export const ThemeContextProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Load saved theme from localStorage
    const saved = localStorage.getItem("flexflow-theme")
    return saved || THEME_OPTIONS.ERP_MINIMAL
  })

  // Save theme to localStorage when changed
  useEffect(() => {
    localStorage.setItem("flexflow-theme", currentTheme)
  }, [currentTheme])

  // Get the MUI theme based on current selection
  const muiTheme = useMemo(() => {
    switch (currentTheme) {
      case THEME_OPTIONS.ERP_COLOR:
        return erpColorTheme
      case THEME_OPTIONS.ERP_ENTERPRISE:
        return erpEnterpriseTheme
      case THEME_OPTIONS.ERP_ELITON:
        return erpElitonTheme
      case THEME_OPTIONS.ERP_MINIMAL:
      default:
        return erpMinimalTheme
    }
  }, [currentTheme])

  const setTheme = (themeName) => {
    if (Object.values(THEME_OPTIONS).includes(themeName)) {
      setCurrentTheme(themeName)
    }
  }

  const value = useMemo(() => ({
    currentTheme,
    setTheme,
    themeOptions: THEME_OPTIONS,
    themeLabels: THEME_LABELS,
  }), [currentTheme])

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

export default ThemeContext
